import { computed, effect, inject } from '@angular/core';
import {
  patchState,
  signalStore,
  withComputed,
  withHooks,
  withMethods,
  withState,
} from '@ngrx/signals';
import { AuthService } from '../core/services/auth.service';
import {
  AuthInfo,
  FirstTimeResetPasswordModel,
  LoginModel,
  PermissionModel,
  UpdatePasswordModel,
} from '../authorization/models';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { EMPTY, from, Observable, pipe, switchMap, tap, throwError } from 'rxjs';
import { catchError, finalize, map, shareReplay, timeout } from 'rxjs/operators';
import { tapResponse } from '@ngrx/operators';
import { Router } from '@angular/router';
import { APP_PATHS, LocalhostKeys, MSAL_SCOPES } from '@core/constants';
import { jwtDecode } from 'jwt-decode';
import { AuthenticationResult } from '@azure/msal-browser';
import { MsalService } from '@azure/msal-angular';
import { CookieAuthService } from '../core/services/cookie-auth.service';
import { resolvePostLoginRedirect } from '../authorization/auth-redirect.util';
import {
  AUTH_ERROR_KEYS,
  decodeEncodedHttpException,
  loginErrorKey,
  parseHttpError,
  resetRequestErrorKey,
  tokenFlowError,
} from '../authorization/auth-error.util';

/** Hard ceiling on a refresh so a stuck token endpoint can never hang the app. */
const REFRESH_TIMEOUT_MS = 15_000;

/** Message the unauthenticated pages render inline; `key` is an i18n key, never raw backend text. */
export type AuthFeedback = { key: string; variant: 'error' | 'success' };

type AuthState = {
  isLoading: boolean;
  authReady: boolean;
  authInfo: AuthInfo;
  isSendingResetPasswordEmail: boolean;
  authFeedback: AuthFeedback | null;
  permissions: PermissionModel[] | undefined;
};

const initialState: AuthState = {
  isLoading: false,
  authReady: false,
  isSendingResetPasswordEmail: false,
  authFeedback: null,
  authInfo: {
    token_type: '',
    expires_in: 0,
    expires_on: undefined,
    ext_expires_in: 0,
    access_token: '',
    refresh_token: '',
    id_token: '',
    isLoginWithMSAL: false,
  },
  permissions: undefined,
};

export const AuthStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withComputed(({ authInfo }) => ({
    isAuthenticated: computed(() => !!authInfo.access_token()),
    // oid is derived from the access token (which always carries it) so it can
    // never be read empty while the user is authenticated. Keeping it in state
    // caused a race: the auth guard opens the app shell as soon as access_token
    // is present, but oid was patched separately (and not at all by
    // updateMsalToken), so PagesComponent could fire getUser('') -> /users/ 404.
    oid: computed(() => {
      const token = authInfo.access_token();
      if (!token) return '';
      try {
        return jwtDecode<{ oid?: string }>(token).oid ?? '';
      } catch {
        return '';
      }
    }),
  })),
  withHooks({
    onInit(store) {
      const authService = inject(AuthService);
      const cookieAuthService = inject(CookieAuthService);
      const localStorageToken = localStorage.getItem(LocalhostKeys.ACCESS_TOKEN);
      if (localStorageToken) {
        const authInfo = JSON.parse(localStorageToken);
        patchState(store, { authInfo });
        cookieAuthService.writeCookie(authInfo);

        // Seed permissions from cache so a refresh renders the nav immediately.
        const localStoragePermissions = localStorage.getItem(LocalhostKeys.PERMISSIONS);
        if (localStoragePermissions) {
          patchState(store, { permissions: JSON.parse(localStoragePermissions) });
        }
      }

      // Load permissions whenever the user is authenticated but we don't have them
      // yet, regardless of WHICH path authenticated them (loginMethod /
      // loggedInViaMSAL / updateMsalToken on ACQUIRE_TOKEN_SUCCESS / refresh).
      // Permissions used to be fetched only in the first three, so a login that
      // resolved via updateMsalToken left them empty -> the permission-gated nav
      // stayed blank until a refresh. The effect runs asynchronously (after
      // construction), which also keeps the fetch off the APP_INITIALIZER stack
      // and avoids the HTTP_INTERCEPTORS -> AuthStore circular DI (NG0200).
      //
      // `permissions() !== undefined` alone only means "we have SOME permissions", not
      // "they belong to the current oid". `permissionsOid` starts `null` on every page load,
      // so the set seeded from `LocalhostKeys.PERMISSIONS` no longer permanently suppresses
      // the fetch the way it used to: previously, once that key existed, this effect returned
      // early forever, so a right granted after this browser's first login never arrived until
      // a fresh/incognito session (RDT-1385: navbar item missing for a user whose permission was
      // confirmed correct in the database). Tracking which oid the cached permissions are for
      // closes that gap, and also covers the same-tab account-switch case.
      let loadingPermissions = false;
      let permissionsOid: string | null = null;

      // Extracted so the error handler can retry directly instead of relying on a state
      // patch to re-trigger the effect. `patchState(store, { permissions: undefined })` is a
      // no-op when `permissions` is already `undefined` (the common case: the in-flight
      // request that just failed never wrote anything), so no tracked signal changes and the
      // effect never re-runs — a fresh oid that arrived while that request was in flight (see
      // `loadingPermissions` bail-out below) is then never fetched. Calling `loadPermissions`
      // straight from the error handler for whatever oid is current closes that gap.
      const loadPermissions = (oid: string) => {
        loadingPermissions = true;
        authService.getUserPermissions(oid).subscribe({
          next: permissions => {
            permissionsOid = oid;
            localStorage.setItem(LocalhostKeys.PERMISSIONS, JSON.stringify(permissions));
            patchState(store, { permissions });
            loadingPermissions = false;
          },
          error: () => {
            permissionsOid = null;
            loadingPermissions = false;
            patchState(store, { permissions: undefined });

            // The oid can have changed again while this request was in flight (the effect
            // bailed out on `loadingPermissions` for it). Retry for whatever oid is current
            // now, rather than waiting on a state change that may never come.
            const currentOid = store.oid();
            if (store.isAuthenticated() && currentOid && currentOid !== oid) {
              loadPermissions(currentOid);
            }
          },
        });
      };

      effect(() => {
        const oid = store.oid();
        if (!store.isAuthenticated() || !oid) return;
        if (store.permissions() !== undefined && permissionsOid === oid) return;
        if (loadingPermissions) return;
        loadPermissions(oid);
      });
    },
  }),
  withMethods(
    (
      store,
      authService = inject(AuthService),
      router = inject(Router),
      cookieAuthService = inject(CookieAuthService),
    ) => ({
      markReady: () => patchState(store, { authReady: true }),

      /** AuthStore is a root singleton, so a failure on one auth page would otherwise
       * still be on screen when the user navigates to the next one. */
      clearAuthFeedback: () => patchState(store, { authFeedback: null }),

      isAccessTokenExpired: (skewSeconds = 30): boolean => {
        const expiresOn = store.authInfo().expires_on;
        return !!expiresOn && expiresOn < Date.now() / 1000 + skewSeconds;
      },

      loginMethod: rxMethod<LoginModel>(
        pipe(
          tap(() => patchState(store, { isLoading: true, authFeedback: null })),
          switchMap(payload => {
            return authService.login(payload).pipe(
              tapResponse({
                next: (authInfo: AuthInfo) => {
                  // A 200 without a token is not a login. The backend answers
                  // {"message":"TOTP Required"} for 2FA accounts, which this portal has no
                  // UI for — without this check the app "succeeds" into an empty session
                  // and the auth guard bounces the user back with nothing explaining why.
                  if (!authInfo?.access_token) {
                    const isTotp = (authInfo as unknown as { message?: string })?.message
                      ?.toLowerCase()
                      .includes('totp');
                    patchState(store, {
                      isLoading: false,
                      authFeedback: {
                        key: isTotp
                          ? AUTH_ERROR_KEYS.twoFactorUnsupported
                          : AUTH_ERROR_KEYS.generic,
                        variant: 'error',
                      },
                    });
                    return;
                  }
                  authInfo.expires_on ??= Date.now() / 1000 + authInfo.expires_in;
                  localStorage.setItem(LocalhostKeys.ACCESS_TOKEN, JSON.stringify(authInfo));
                  cookieAuthService.writeCookie(authInfo);
                  patchState(store, { authInfo, authReady: true });
                  // Permissions load via the effect in onInit (triggered by the
                  // authInfo change above) — no need to fetch them here.

                  // Navigate right away; keep the spinner up until navigation settles so
                  // the bare login form is never shown after a successful sign-in (BUG 1).
                  const redirectUrl = globalThis.sessionStorage.getItem('redirect_uri');
                  globalThis.sessionStorage.removeItem('redirect_uri');
                  void resolvePostLoginRedirect(router, redirectUrl).finally(() =>
                    patchState(store, { isLoading: false }),
                  );
                },
                error: (error: unknown) => {
                  patchState(store, {
                    isLoading: false,
                    authFeedback: { key: loginErrorKey(error), variant: 'error' },
                  });
                },
              }),
            );
          }),
        ),
      ),
      refreshToken: () => {
        return authService.getRefreshToken(store.authInfo.refresh_token()).pipe(
          tap((authInfo: AuthInfo) => {
            authInfo.expires_on ??= Date.now() / 1000 + authInfo.expires_in;
            localStorage.setItem(LocalhostKeys.ACCESS_TOKEN, JSON.stringify(authInfo));
            cookieAuthService.writeCookie(authInfo);
            patchState(store, { authInfo });
          }),
        );
      },
      updateMsalToken: (result: AuthenticationResult) => {
        const authInfo: AuthInfo = {
          ...store.authInfo(),
          access_token: result.accessToken,
          id_token: result.idToken,
          expires_on: (result.expiresOn?.getTime() ?? 0) / 1000,
          expires_in: result.expiresOn
            ? Math.floor((result.expiresOn.getTime() - Date.now()) / 1000)
            : 0,
          ext_expires_in: result.extExpiresOn
            ? Math.floor((result.extExpiresOn.getTime() - Date.now()) / 1000)
            : 0,
        };
        localStorage.setItem(LocalhostKeys.ACCESS_TOKEN, JSON.stringify(authInfo));
        cookieAuthService.writeCookie(authInfo);
        patchState(store, { authInfo });
      },
      loggedInViaMSAL: rxMethod<any>(
        pipe(
          tap(msg => {
            const data = (msg as any).payload as AuthenticationResult;
            const authInfo: AuthInfo = {
              token_type: data.tokenType,
              access_token: data.accessToken,
              refresh_token: '',
              expires_on: (data.expiresOn?.getTime() ?? 0) / 1000,
              expires_in: data.expiresOn
                ? Math.floor((data.expiresOn.getTime() - Date.now()) / 1000)
                : 0,
              ext_expires_in: data.extExpiresOn
                ? Math.floor((data.extExpiresOn.getTime() - Date.now()) / 1000)
                : 0,
              id_token: data.idToken,
              isLoginWithMSAL: true,
            };
            localStorage.setItem(LocalhostKeys.ACCESS_TOKEN, JSON.stringify(authInfo));
            cookieAuthService.writeCookie(authInfo);
            patchState(store, { authInfo, isLoading: false, authReady: true });
            // Permissions load via the effect in onInit (triggered by the authInfo change
            // above). Navigation is intentionally NOT done here: the /auth-callback route
            // guard (authCallbackGuard) owns the post-SSO landing as a single navigation.
            // Redirecting from this event handler too raced the callback's default-route
            // navigation and dropped the deep link, and it never ran at all on a silent
            // ACQUIRE_TOKEN_SUCCESS re-auth. Setting authReady above lets the guard resolve.
          }),
        ),
      ),

      resetPasswordMethod: rxMethod<FirstTimeResetPasswordModel>(
        pipe(
          tap(() => patchState(store, { isLoading: true, authFeedback: null })),
          switchMap(payload => {
            return authService.resetPassword(payload).pipe(
              tapResponse({
                next: (response: unknown) => {
                  if (decodeEncodedHttpException(response)) {
                    patchState(store, {
                      isLoading: false,
                      authFeedback: { key: AUTH_ERROR_KEYS.generic, variant: 'error' },
                    });
                    return;
                  }
                  patchState(store, { isLoading: false });
                  router.navigate(['/', APP_PATHS.Auth, APP_PATHS.SetNewPasswordSuccess]);
                },
                error: (error: unknown) => {
                  const { key, isDeadLink } = tokenFlowError(error);
                  patchState(store, {
                    isLoading: false,
                    authFeedback: isDeadLink ? null : { key, variant: 'error' },
                  });
                  if (isDeadLink) {
                    router.navigate(['/', APP_PATHS.Auth, APP_PATHS.LinkExpired]);
                  }
                },
              }),
            );
          }),
        ),
      ),

      updatePasswordMethod: rxMethod<UpdatePasswordModel>(
        pipe(
          tap(() => patchState(store, { isLoading: true })),
          switchMap(payload => {
            return authService.updatePassword(payload).pipe(
              tapResponse({
                next: () => {
                  router.navigate(['/', APP_PATHS.Auth, APP_PATHS.SetNewPasswordSuccess]);
                },
                error: () => {
                  patchState(store, {
                    isLoading: false,
                  });
                },
              }),
            );
          }),
        ),
      ),

      sendLinkResetPasswordToEmailMethod: rxMethod<string>(
        pipe(
          tap(() => patchState(store, { isSendingResetPasswordEmail: true, authFeedback: null })),
          switchMap(email => {
            const goToSuccess = () => {
              patchState(store, { isSendingResetPasswordEmail: false });
              router.navigate(['/', APP_PATHS.Auth, APP_PATHS.ResetPasswordSuccess], {
                state: { resetPasswordEmail: email },
              });
            };

            return authService.sendLinkResetPasswordToEmail(email).pipe(
              tapResponse({
                next: goToSuccess,
                error: (error: unknown) => {
                  // The backend answers 404 for an unknown address. Reporting that back
                  // would turn this form into an account-enumeration oracle, so an unknown
                  // address gets the same "check your email" page as a known one.
                  if (parseHttpError(error).status === 404) {
                    goToSuccess();
                    return;
                  }
                  patchState(store, {
                    isSendingResetPasswordEmail: false,
                    authFeedback: { key: resetRequestErrorKey(error), variant: 'error' },
                  });
                },
              }),
            );
          }),
        ),
      ),

      logout: rxMethod<void>(
        pipe(
          switchMap(() => {
            const accessToken = store.authInfo().access_token; // the API expects the access token as the id token

            return authService.logout(accessToken).pipe(
              catchError(() => EMPTY),
              finalize(() => {
                patchState(store, { ...initialState, authReady: true });
                localStorage.removeItem(LocalhostKeys.PERMISSIONS);
                localStorage.removeItem(LocalhostKeys.ACCESS_TOKEN);
                cookieAuthService.clearCookie();
                router.navigate(['/', APP_PATHS.Auth, APP_PATHS.Login]);
              }),
            );
          }),
        ),
      ),

      joinNetwork: rxMethod<string>(
        pipe(
          tap(() => patchState(store, { isLoading: true, authFeedback: null })),
          switchMap(token => {
            return authService.joinNetwork(token).pipe(
              tapResponse({
                next: () => {
                  patchState(store, {
                    isLoading: false,
                    authFeedback: { key: 'joinNetwork.success.desc', variant: 'success' },
                  });
                },
                error: (error: unknown) => {
                  const { key, isDeadLink } = tokenFlowError(error);
                  patchState(store, {
                    isLoading: false,
                    authFeedback: isDeadLink ? null : { key, variant: 'error' },
                  });
                  if (isDeadLink) {
                    router.navigate(['/', APP_PATHS.Auth, APP_PATHS.LinkExpired]);
                  }
                },
              }),
            );
          }),
        ),
      ),
    }),
  ),
  withMethods((store, msalService = inject(MsalService)) => {
    // Single-flight refresh shared by the HTTP interceptor (per request) and the auth
    // guard (per navigation): ONE in-flight refresh that every concurrent caller
    // subscribes to, owned by this singleton store so a cancelled trigger can't kill
    // it. `null` = nothing in flight (also the single-flight gate).
    let refresh$: Observable<string> | null = null;

    // Password sessions refresh via the backend token endpoint.
    const refreshViaToken = (): Observable<string> =>
      store.refreshToken().pipe(map(info => info.access_token));

    // SSO sessions refresh via MSAL silent acquisition — their refresh token lives in
    // MSAL's own cache, NOT in authInfo. initialize() first so this is safe to call
    // from the guard before MSAL has finished booting (idempotent once initialized).
    const refreshViaMsal = (): Observable<string> =>
      from(msalService.instance.initialize()).pipe(
        switchMap(() => {
          const account =
            msalService.instance.getAccountByLocalId(store.oid()) ??
            msalService.instance.getAllAccounts()[0];
          return msalService.acquireTokenSilent({ scopes: MSAL_SCOPES, account });
        }),
        map((result: AuthenticationResult) => {
          store.updateMsalToken(result);
          return result.accessToken;
        }),
      );

    return {
      /**
       * Resolve to a valid access token, refreshing first if needed. Used by the
       * interceptor (proactive + reactive 401 retry) and the auth guard (expired token
       * on navigation). Any failure (rejected token, timeout, no MSAL session) is
       * terminal -> logout. shareReplay(refCount:false) keeps the refresh alive until
       * it settles and replays to late/queued subscribers; finalize reopens the gate.
       */
      ensureFreshToken: (): Observable<string> => {
        if (!refresh$) {
          const source = store.authInfo().isLoginWithMSAL ? refreshViaMsal() : refreshViaToken();
          refresh$ = source.pipe(
            timeout(REFRESH_TIMEOUT_MS),
            catchError(err => {
              store.logout();
              return throwError(() => err);
            }),
            finalize(() => {
              refresh$ = null;
            }),
            shareReplay({ bufferSize: 1, refCount: false }),
          );
        }
        return refresh$;
      },
    };
  }),
);
